/**
 * QR Code Service
 *
 * NOTE: QR code scanning is now handled by the CameraStream component
 * using @yudiel/react-qr-scanner library. This service is kept for
 * processing uploaded QR code images (from gallery).
 */

import QrScanner from 'qr-scanner';

/**
 * Process QR code from uploaded image file (gallery upload)
 * @param {File} imageFile - The image file containing QR code
 * @returns {Promise<string|null>} - QR code data or null if not found
 */
export const processQRCodeImage = async (imageFile) => {
    try {
        console.log('[QR Service] Starting QR code detection...');
        console.log('[QR Service] File type:', imageFile.type);
        console.log('[QR Service] File size:', imageFile.size, 'bytes');

        const imageUrl = URL.createObjectURL(imageFile);

        try {
            // Load image first
            const img = new Image();
            img.src = imageUrl;

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
            });

            console.log('[QR Service] Image loaded:', img.width, 'x', img.height);

            // Try 1: Scan with canvas (most reliable approach)
            console.log('[QR Service] Attempting canvas-based scan...');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { willReadFrequently: true });

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            try {
                const canvasResult = await QrScanner.scanImage(canvas, {
                    returnDetailedScanResult: true,
                    alsoTryWithoutScanRegion: true
                });

                if (canvasResult && canvasResult.data) {
                    console.log('[QR Service] QR code found with canvas scan:', canvasResult.data);
                    return canvasResult.data;
                }
            } catch (error) {
                console.log('[QR Service] Canvas scan failed:', error.message);
            }

            // Try 2: Scan image element directly
            console.log('[QR Service] Attempting direct image element scan...');
            try {
                const imgResult = await QrScanner.scanImage(img, {
                    returnDetailedScanResult: true,
                    alsoTryWithoutScanRegion: true
                });

                if (imgResult && imgResult.data) {
                    console.log('[QR Service] QR code found with image scan:', imgResult.data);
                    return imgResult.data;
                }
            } catch (error) {
                console.log('[QR Service] Image element scan failed:', error.message);
            }

            // Try 3: Scan with inverted colors (for QR codes with dark backgrounds)
            console.log('[QR Service] Attempting inverted scan...');
            const invertedCanvas = document.createElement('canvas');
            const invertedCtx = invertedCanvas.getContext('2d', { willReadFrequently: true });

            invertedCanvas.width = img.width;
            invertedCanvas.height = img.height;
            invertedCtx.drawImage(img, 0, 0);

            // Invert the image colors
            const imageData = invertedCtx.getImageData(0, 0, invertedCanvas.width, invertedCanvas.height);
            for (let i = 0; i < imageData.data.length; i += 4) {
                imageData.data[i] = 255 - imageData.data[i];       // R
                imageData.data[i + 1] = 255 - imageData.data[i + 1]; // G
                imageData.data[i + 2] = 255 - imageData.data[i + 2]; // B
            }
            invertedCtx.putImageData(imageData, 0, 0);

            try {
                const invertedResult = await QrScanner.scanImage(invertedCanvas, {
                    returnDetailedScanResult: true,
                    alsoTryWithoutScanRegion: true
                });

                if (invertedResult && invertedResult.data) {
                    console.log('[QR Service] QR code found with inverted scan:', invertedResult.data);
                    return invertedResult.data;
                }
            } catch (error) {
                console.log('[QR Service] Inverted scan failed:', error.message);
            }

            // Try 4: Scan file blob directly
            console.log('[QR Service] Attempting direct file blob scan...');
            try {
                const fileResult = await QrScanner.scanImage(imageFile, {
                    returnDetailedScanResult: true,
                    alsoTryWithoutScanRegion: true
                });

                if (fileResult && fileResult.data) {
                    console.log('[QR Service] QR code found with file scan:', fileResult.data);
                    return fileResult.data;
                }
            } catch (error) {
                console.log('[QR Service] File blob scan failed:', error.message);
            }

            console.log('[QR Service] No QR code found after all attempts');
            return null;
        } finally {
            URL.revokeObjectURL(imageUrl);
        }
    } catch (error) {
        console.error('[QR Service] Error processing QR code:', error);
        throw error;
    }
};

/**
 * Validate if a string is a valid UUID
 * @param {string} str - The string to validate
 * @returns {boolean} - True if valid UUID, false otherwise
 */
const isValidUUID = (str) => {
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidPattern.test(str);
};

/**
 * Send QR code value to backend
 * @param {string} qrValue - The QR code value to send
 * @returns {Promise<Object>} - API response
 */
export const sendQRCodeValue = async (qrValue) => {
    try {
        // Validate UUID format before sending
        if (!isValidUUID(qrValue)) {
            console.error('[QR Service] Invalid QR code format - not a valid UUID:', qrValue);
            return {
                status: 'error',
                message: 'Invalid QR code format'
            };
        }

        console.log('[QR Service] Valid UUID detected, sending to backend:', qrValue);

        const response = await fetch('/module/order/verifyCode', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code: qrValue }),
        });
        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error sending QR code value:', error);
        throw error;
    }
};
